// Making a phone photo small enough to send.
//
// A photo of an ID card or a CPR card taken on a phone is 3–12 MB, and the
// public pages send files as base64 inside a JSON request, which adds a third
// again. Over mobile data that is a minute or more per file, and on
// 27 October 2026 an applicant (Christina Chambers) tried four times: each
// time the small files arrived and the photos never did, so nothing was saved.
//
// A document photo needs to be legible, not print-quality. Scaled to 2000 px on
// its long side and saved as JPEG at 0.82, a 9 MB photo is about 400 KB and
// still reads cleanly. PDFs and Word files are left exactly as they are —
// re-encoding a scanned PDF is not this module's business.
//
// Browser only (canvas). Never throws: if the browser cannot decode the image
// (an unusual format, an old phone) the original file is returned unchanged.

export const MAX_EDGE_PX = 2000;
export const JPEG_QUALITY = 0.82;

/** Is this a photo the browser can redraw? GIFs and SVGs are left alone. */
export const isShrinkableImage = (file) =>
  !!file && /^image\/(jpeg|jpg|png|webp|heic|heif)$/i.test(file.type || '');

/** The size a w×h image is drawn at, its long side capped at maxEdge. */
export const scaledSize = (w, h, maxEdge = MAX_EDGE_PX) => {
  const long = Math.max(w, h);
  if (!long || long <= maxEdge) return { width: w, height: h };
  const k = maxEdge / long;
  return { width: Math.round(w * k), height: Math.round(h * k) };
};

const loadImage = async (file) => {
  if (typeof createImageBitmap === 'function') {
    try { return await createImageBitmap(file, { imageOrientation: 'from-image' }); } catch { /* fall through */ }
  }
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
    img.src = url;
  });
};

/**
 * A smaller copy of a photo, or the file itself.
 *
 * Returns the original when it is not a photo, when it cannot be decoded, or
 * when the copy would not be meaningfully smaller.
 */
export const shrinkForUpload = async (file) => {
  if (!isShrinkableImage(file)) return file;
  try {
    const img = await loadImage(file);
    const w = img.width || img.naturalWidth;
    const h = img.height || img.naturalHeight;
    const { width, height } = scaledSize(w, h);
    const canvas = document.createElement('canvas');
    canvas.width = width; canvas.height = height;
    const ctx = canvas.getContext('2d');
    // White under a transparent PNG, so it does not turn black as a JPEG.
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, width, height);
    ctx.drawImage(img, 0, 0, width, height);
    if (typeof img.close === 'function') img.close();
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY));
    if (!blob || blob.size >= file.size * 0.9) return file;
    const name = file.name.replace(/\.[^.]+$/, '') + '.jpg';
    return new File([blob], name, { type: 'image/jpeg', lastModified: Date.now() });
  } catch {
    return file;
  }
};
